import { SiteResponse } from "../types";
import { EditSiteFormValues } from "../components/Form/editSiteValidationSchema";

type DeliveryConfigFormValues = Pick<
  EditSiteFormValues,
  | "leadTimeDays"
  | "deliveryWindows"
  | "deliveryRequirements"
  | "defaultRecipients"
>;

/** The four OC delivery-config fields as `PUT /site/{id}` carries them. */
export interface SiteDeliveryConfigPayload {
  deliveryWindows: string[];
  leadTimeDays: number | null;
  deliveryRequirements: { title: string; detail: string }[];
  defaultRecipients: { name: string; email: string }[];
}

/**
 * Site → RHF defaults for the delivery-config section (Task 4.3). The BE fields are nullable, so
 * every one falls back to an empty value; windows become `{value}` rows because `useFieldArray`
 * can't hold bare strings.
 */
export const siteToDeliveryConfigDefaults = (
  site?: SiteResponse | null
): Required<DeliveryConfigFormValues> => ({
  leadTimeDays:
    site?.leadTimeDays === null || site?.leadTimeDays === undefined
      ? ""
      : String(site.leadTimeDays),
  deliveryWindows: (site?.deliveryWindows ?? []).map((value) => ({ value })),
  deliveryRequirements: (site?.deliveryRequirements ?? []).map((r) => ({
    title: r.title ?? "",
    detail: r.detail ?? "",
  })),
  defaultRecipients: (site?.defaultRecipients ?? []).map((r) => ({
    name: r.name ?? "",
    email: r.email ?? "",
  })),
});

/**
 * RHF values → the payload fields riding the EXISTING Site save (no separate endpoint). Always
 * sent, even when empty: `PUT /site/{id}` is a full replacement, so omitting them would be
 * indistinguishable from "leave unchanged" at best and "wipe" at worst — an empty list / null
 * lead time is the explicit way to clear the config.
 */
export const deliveryConfigToPayload = (
  values: DeliveryConfigFormValues
): SiteDeliveryConfigPayload => {
  const lead = values.leadTimeDays?.trim();
  return {
    deliveryWindows: (values.deliveryWindows ?? [])
      .map((w) => w.value.trim())
      .filter(Boolean),
    leadTimeDays: lead ? Number(lead) : null,
    deliveryRequirements: (values.deliveryRequirements ?? []).map((r) => ({
      title: r.title.trim(),
      detail: r.detail?.trim() ?? "",
    })),
    defaultRecipients: (values.defaultRecipients ?? []).map((r) => ({
      name: r.name?.trim() ?? "",
      email: r.email.trim(),
    })),
  };
};
